import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface AssessmentSkillScores {
  communication: number;
  technical: number;
  punctuality: number;
  teamwork: number;
}

export interface AssessmentKPIMetrics {
  tasks_completed: number;
  on_time: boolean;
  quality_score: number;
}

export interface PartnerAssessment {
  id: number;
  work_log_id: number;
  student_name: string;
  partner_name: string;
  task_title: string;
  rating: number;
  skill_scores: AssessmentSkillScores;
  kpi_metrics: AssessmentKPIMetrics;
  feedback: string;
  assessed_by: string;
  assessed_at: string;
}

export interface WorkLogForAssessment {
  id: string;
  studentName: string;
  student_id: number;
  date: string;
  hours: number;
  description: string;
  status: string;
}

export interface AssessmentSubmitPayload {
  work_log_id: number;
  rating: number;
  skill_scores: AssessmentSkillScores;
  kpi_metrics: AssessmentKPIMetrics;
  feedback: string;
  isDemo?: boolean;
}

// ─── Demo Data ────────────────────────────────────────────────────────────────

export const DEMO_STUDENT_ASSESSMENTS: PartnerAssessment[] = [
  {
    id: 1,
    work_log_id: 101,
    student_name: "Amina Juma",
    partner_name: "Tanzania Tech Hub",
    task_title: "Dashboard API Integration",
    rating: 4,
    skill_scores: { communication: 5, technical: 4, punctuality: 4, teamwork: 4 },
    kpi_metrics: { tasks_completed: 12, on_time: true, quality_score: 4.2 },
    feedback:
      "Excellent work on the API integration. Clean code, good documentation, and strong communication throughout.",
    assessed_by: "Dr. Sarah Mushi",
    assessed_at: "2026-05-10T09:00:00Z",
  },
  {
    id: 2,
    work_log_id: 98,
    student_name: "Amina Juma",
    partner_name: "Tanzania Tech Hub",
    task_title: "User Authentication Module",
    rating: 5,
    skill_scores: { communication: 5, technical: 5, punctuality: 5, teamwork: 4 },
    kpi_metrics: { tasks_completed: 8, on_time: true, quality_score: 4.8 },
    feedback:
      "Outstanding implementation. Best work this quarter. Deep understanding of security principles and excellent attention to detail.",
    assessed_by: "Dr. Sarah Mushi",
    assessed_at: "2026-05-05T11:00:00Z",
  },
  {
    id: 3,
    work_log_id: 95,
    student_name: "Amina Juma",
    partner_name: "NMB Bank",
    task_title: "Database Optimization",
    rating: 3,
    skill_scores: { communication: 3, technical: 4, punctuality: 3, teamwork: 3 },
    kpi_metrics: { tasks_completed: 5, on_time: false, quality_score: 3.5 },
    feedback:
      "Good technical foundation but needs to improve on meeting deadlines. Communication could be more proactive.",
    assessed_by: "Mr. James Kilonzo",
    assessed_at: "2026-04-28T14:30:00Z",
  },
];

export const DEMO_PARTNER_PENDING_WORKLOGS: WorkLogForAssessment[] = [
  {
    id: "wl-101",
    studentName: "Amina Juma",
    student_id: 101,
    date: "2026-05-12",
    hours: 8,
    description: "Dashboard API Integration",
    status: "pending",
  },
  {
    id: "wl-102",
    studentName: "John Msafiri",
    student_id: 102,
    date: "2026-05-11",
    hours: 7.5,
    description: "Frontend Component Development",
    status: "pending",
  },
  {
    id: "wl-103",
    studentName: "Grace Mwakyoma",
    student_id: 103,
    date: "2026-05-10",
    hours: 8,
    description: "Unit Testing Implementation",
    status: "pending",
  },
];

// ─── Student Hooks ─────────────────────────────────────────────────────────────

export function useStudentAssessments() {
  const { user } = useAuthStore();
  return useQuery<PartnerAssessment[]>({
    queryKey: ["student-assessments"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_STUDENT_ASSESSMENTS;
      const res = await apiClient.get("/students/me/assessments");
      return res.data.data || [];
    },
    initialData: user?.isDemo ? DEMO_STUDENT_ASSESSMENTS : undefined,
  });
}

// ─── Partner Hooks ─────────────────────────────────────────────────────────────

export function usePartnerAssessments() {
  const { user } = useAuthStore();
  return useQuery<PartnerAssessment[]>({
    queryKey: ["partner-assessments"],
    queryFn: async () => {
      if (user?.isDemo) return [];
      const res = await apiClient.get("/partners/me/work-logs");
      return unwrapApiArray<any>(res)
        .filter((log) => log.status === "approved" && (log.feedback || log.reviewed_at))
        .map((log) => ({
          id: Number(log.id),
          work_log_id: Number(log.id),
          student_name: log.student_name || "Student",
          partner_name: log.partner_name || log.placement_title || "Partner",
          task_title: log.placement_title || log.title || log.description || "Reviewed work log",
          rating: Number(log.rating || 0),
          skill_scores: log.skill_scores || undefined,
          kpi_metrics: log.kpi_metrics || undefined,
          feedback: log.feedback || "",
          assessed_by: String(log.reviewed_by || ""),
          assessed_at: log.reviewed_at || log.updated_at || log.created_at,
        }));
    },
    initialData: user?.isDemo ? [] : undefined,
  });
}

export function usePendingWorkLogsForAssessment() {
  const { user } = useAuthStore();
  return useQuery<WorkLogForAssessment[]>({
    queryKey: ["pending-work-logs-assessment"],
    queryFn: async () => {
      if (user?.isDemo) return DEMO_PARTNER_PENDING_WORKLOGS;
      const res = await apiClient.get("/partners/me/work-logs?status=approved&for_assessment=true");
      return unwrapApiArray<any>(res)
        .filter((log) => log.status === "approved" && !log.feedback)
        .map((log) => ({
          id: String(log.id),
          studentName: log.student_name || "Student",
          student_id: Number(log.student_id || 0),
          date: log.date || log.created_at,
          hours: Number(log.hours || 0),
          description: log.placement_title || log.content || "Approved work log",
          status: log.status,
        }));
    },
    initialData: user?.isDemo ? DEMO_PARTNER_PENDING_WORKLOGS : undefined,
  });
}

export function useSubmitAssessment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: AssessmentSubmitPayload) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 800));
        return {
          id: Date.now(),
          work_log_id: data.work_log_id,
          student_name: "Amina Juma",
          partner_name: "Tanzania Tech Hub",
          task_title: "Reviewed work log",
          rating: data.rating,
          skill_scores: data.skill_scores,
          feedback: data.feedback,
          assessed_by: "You",
          assessed_at: new Date().toISOString(),
        };
      }
      const res = await apiClient.post(`/partners/me/work-logs/${data.work_log_id}/review`, {
        status: "approved",
        feedback: data.feedback,
      });
      return unwrapApiObject(res);
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["student-assessments"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
        queryClient.setQueryData(["pending-work-logs-assessment"], (old: any[]) =>
          old?.filter((l: any) => Number(l.id) !== Number(result.work_log_id))
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["student-assessments"] });
        queryClient.invalidateQueries({ queryKey: ["partner-assessments"] });
        queryClient.invalidateQueries({ queryKey: ["pending-work-logs-assessment"] });
      }
    },
  });
}
